import React from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../components/ui/Button.tsx';
import GradientText from '../components/ui/GradientText.tsx';
import SEO from '../components/SEO.tsx';
import { Mail, Rocket } from 'lucide-react';
import DemoVideoPopup from '../components/ui/DemoVideoPopup.tsx';

interface RoadmapProps {
  onNavigate: (page: string) => void;
}

const Roadmap: React.FC<RoadmapProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const roadmapItems = [
    {
      quarter: "Q1 2024",
      status: 'completed',
      title: t('roadmap.quarters.q1_2024.title'),
      description: t('roadmap.quarters.q1_2024.description'),
      features: t('roadmap.quarters.q1_2024.features', { returnObjects: true }) as string[]
    },
    {
      quarter: "Q2 2025",
      status: 'completed',
      title: t('roadmap.quarters.q2_2024.title'),
      description: t('roadmap.quarters.q2_2024.description'),
      features: t('roadmap.quarters.q2_2024.features', { returnObjects: true }) as string[]
    },
    {
      quarter: "Q3 2025",
      status: 'completed',
      title: t('roadmap.quarters.q3_2024.title'),
      description: t('roadmap.quarters.q3_2024.description'),
      features: t('roadmap.quarters.q3_2024.features', { returnObjects: true }) as string[]
    },
    {
      quarter: "Q2 2026",
      status: 'in-progress',
      title: t('roadmap.quarters.q1_2025.title'),
      description: t('roadmap.quarters.q1_2025.description'),
      features: t('roadmap.quarters.q1_2025.features', { returnObjects: true }) as string[]
    },
    {
      quarter: "Q4 2025",
      status: 'planned',
      title: t('roadmap.quarters.q4_2024.title'),
      description: t('roadmap.quarters.q4_2024.description'),
      features: t('roadmap.quarters.q4_2024.features', { returnObjects: true }) as string[]
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500';
      case 'in-progress':
        return 'bg-kexa-teal';
      case 'planned':
        return 'bg-gray-400';
      default:
        return 'bg-gray-400';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed':
        return t('common.status.completed');
      case 'in-progress':
        return t('common.status.inProgress');
      case 'planned':
        return t('common.status.planned');
      default:
        return t('common.status.planned');
    }
  };

  return (
    <div className="min-h-screen pt-16">
      <DemoVideoPopup />
      <SEO
        title="Kexa Premium Roadmap - Upcoming Cloud Security Features"
        description="Discover what's coming next in Kexa Premium. Explore our roadmap of upcoming features, enhancements, and new cloud security capabilities."
        keywords="kexa roadmap, cloud security features, upcoming releases, product development, security platform updates"
      />
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            {t('roadmap.title.prefix')}
            <GradientText variant="primary">{t('roadmap.title.highlight')}</GradientText>
            {t('roadmap.title.suffix')}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('roadmap.subtitle')}
          </p>
        </div>
        <div className="relative">
          {/* Mobile Timeline - Vertical */}
          <div className="block lg:hidden">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gray-300"></div>
            <div className="space-y-8">
              {roadmapItems.map((item, index) => (
                <div key={index} className="relative flex items-start">
                  <div className={`absolute left-6 w-4 h-4 rounded-full ${getStatusColor(item.status)} border-4 border-white shadow-md`}></div>
                  <div className="ml-16 bg-white rounded-xl shadow-lg p-6 w-full">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="text-sm font-medium text-kexa-teal bg-kexa-teal bg-opacity-10 px-3 py-1 rounded-full">
                        {item.quarter}
                      </span>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                        item.status === 'completed' 
                          ? 'text-green-700 bg-green-100' 
                          : item.status === 'in-progress'
                          ? 'text-kexa-teal bg-kexa-teal bg-opacity-10'
                          : 'text-gray-700 bg-gray-100'
                      }`}>
                        {getStatusText(item.status)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {item.description}
                    </p>
                    
                    <div className="grid grid-cols-1 gap-2">
                      {item.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(item.status)}`}></div>
                          <span className="text-gray-700 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Desktop Timeline - Horizontal */}
          <div className="hidden lg:block">
            <div className="absolute top-20 left-0 right-0 h-0.5 bg-gray-300"></div>
            <div className="grid grid-cols-5 gap-4">
              {roadmapItems.map((item, index) => (
                <div key={index} className="relative">
                  <div className={`absolute top-16 left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full ${getStatusColor(item.status)} border-4 border-white shadow-md z-10`}></div>
                  <div className="bg-white rounded-xl shadow-lg p-4 mt-28">
                    <div className="text-center mb-3">
                      <span className="text-sm font-medium text-kexa-teal bg-kexa-teal bg-opacity-10 px-3 py-1 rounded-full">
                        {item.quarter}
                      </span>
                    </div>
                    <div className="text-center mb-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        item.status === 'completed' 
                          ? 'text-green-700 bg-green-100' 
                          : item.status === 'in-progress'
                          ? 'text-kexa-teal bg-kexa-teal bg-opacity-10'
                          : 'text-gray-700 bg-gray-100'
                      }`}>
                        {getStatusText(item.status)}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 text-center">
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 text-center">
                      {item.description}
                    </p>
                    <div className="space-y-2">
                      {item.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center space-x-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${getStatusColor(item.status)} flex-shrink-0`}></div>
                          <span className="text-gray-700 text-xs">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-20 bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              {t('roadmap.featureRequest.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('roadmap.featureRequest.subtitle')}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-14 h-14 bg-gradient-to-r from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('roadmap.featureRequest.categories.shareIdeas.title')}</h3>
              <p className="text-gray-600">{t('roadmap.featureRequest.categories.shareIdeas.description')}</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-14 h-14 bg-gradient-to-r from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('roadmap.featureRequest.categories.joinDiscussions.title')}</h3>
              <p className="text-gray-600">{t('roadmap.featureRequest.categories.joinDiscussions.description')}</p>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="w-14 h-14 bg-gradient-to-r from-primary to-accent rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{t('roadmap.featureRequest.categories.betaAccess.title')}</h3>
              <p className="text-gray-600">{t('roadmap.featureRequest.categories.betaAccess.description')}</p>
            </div>
          </div>
        </div>
        </div>
      </section>
        <div className="mt-16 py-20 bg-gradient-hero relative overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-8 animate-slide-up">
              {t('common.buttons.readyToGetStartedWithFeatures')}
            </h2>
            <div className="w-16 h-px bg-white/40 mx-auto mb-8 animate-fade-in"></div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in">
            <Button
              onClick={() => onNavigate('plans')}
              variant="cta"
              size="lg"
              leftIcon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>}
            >
              {t('common.buttons.explorePlans')}
            </Button>
            <Button
              onClick={() => onNavigate('contact')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Mail size={20} />}
            >
              {t('common.buttons.contactUs')}
            </Button>
            <Button
              onClick={() => onNavigate('deploy')}
              variant="cta-glass"
              size="lg"
              rightIcon={<Rocket size={20} />}
            >
              {t('common.buttons.startDeploying')}
            </Button>
            </div>
          </div>
        </div>
    </div>
  );
};

export default Roadmap;